import { Button, Modal, notification as antNotification } from "antd";
import { audioNotification as notification } from "./audioNotification";
import { playErrorSound } from "./soundUtils";
import { navigateRef } from "@utils/navigate_ref";
import "@styles/styles-TeamValidationToast.css";

type Severity = "error" | "warning" | "info";

type ValidationMessage = {
  message?: string;
  summary?: string;
  items?: string[];
  severity?: Severity;
  category?: string;
  integration_key?: string;
  provider?: string;
  action?: "settings" | "support";
};

const INTEGRATION_KEY_TOAST_KEY = "integration-key-validation";
const TEAM_SETUP_TOAST_KEY = "team-setup-validation";
const SEVERITY_RANK: Record<Severity, number> = { info: 0, warning: 1, error: 2 };
const EXTERNAL_DATA_CONNECTIONS_PATH = "/settings/third-party-integrations";
const listFormat = new Intl.ListFormat("en", { style: "long", type: "conjunction" });

let integrationToastOpen = false;

const closeIntegrationToast = () => {
  integrationToastOpen = false;
  antNotification.destroy(INTEGRATION_KEY_TOAST_KEY);
};

const openExternalDataConnections = () => {
  closeIntegrationToast();
  if (navigateRef.current) {
    navigateRef.current(EXTERNAL_DATA_CONNECTIONS_PATH);
  } else {
    window.location.assign(EXTERNAL_DATA_CONNECTIONS_PATH);
  }
};

// antd stacks more than three notifications and shows only the last one, so one
// toast carries every invalid key. The fixed key replaces the toast on each
// team-validation call, duration 0 keeps it open until the user closes it, and
// the error sound plays only when the toast was not already open.
// A user without the permission gets no button, because the settings tab refuses them.
const displayIntegrationKeyErrors = (errors: ValidationMessage[], canManageIntegrations: boolean) => {
  const names = listFormat.format(errors.map((e) => e.provider || e.integration_key || ""));
  const selfServe = errors.some((e) => e.action === "settings");
  const plural = errors.length > 1;
  const updater = selfServe ? "Ask a teammate with access to External Data Connections" : "Contact Whistle support";
  const description = selfServe && canManageIntegrations ? (
    <div className="integration-key-toast-row">
      <span>{names}</span>
      <Button type="primary" size="small" onClick={openExternalDataConnections}>
        Open settings
      </Button>
    </div>
  ) : (
    `${names}. ${updater} to update ${plural ? "them" : "it"}.`
  );
  if (!integrationToastOpen) playErrorSound();
  integrationToastOpen = true;
  antNotification.error({
    key: INTEGRATION_KEY_TOAST_KEY,
    duration: 0,
    message: plural ? `${errors.length} API keys are no longer valid` : "An API key is no longer valid",
    description,
    onClose: () => {
      integrationToastOpen = false;
    },
  });
};

// The details modal lists every name or metric behind the counts in the toast.
const showTeamSetupDetails = (messages: ValidationMessage[]) => {
  Modal.info({
    title: "Team setup issues",
    width: 420,
    okText: "Close",
    content: (
      <div className="team-setup-details">
        {messages.map((e, index) => (
          <section key={index}>
            <p>{e.summary || e.message}</p>
            {e.items?.length ? (
              <ul>
                {e.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>
    ),
  });
};

// Every message that is not about an API key shares the second toast, so a team
// never sees more than two boxes from one team-validation call. The fixed key
// replaces the toast on each call and the toast takes the highest severity.
// The toast shows the short `summary` line of each message and keeps the names
// and metrics for the details modal, so its height does not grow with the roster.
// Duration 0 keeps it open long enough to reach the button; the next call
// without messages closes it.
const displayTeamSetupMessages = (messages: ValidationMessage[]) => {
  if (!messages.length) {
    antNotification.destroy(TEAM_SETUP_TOAST_KEY);
    return;
  }
  const severity = messages.reduce<Severity>(
    (worst, e) => (SEVERITY_RANK[e.severity ?? "info"] > SEVERITY_RANK[worst] ? e.severity ?? "info" : worst),
    "info"
  );
  const single = messages.length === 1;
  const hasDetails = messages.some((e) => e.items?.length);
  notification[severity]({
    key: TEAM_SETUP_TOAST_KEY,
    duration: 0,
    message: single ? undefined : `${messages.length} team setup issues`,
    description: (
      <div className="team-setup-toast">
        {single ? (
          <span>{messages[0].summary || messages[0].message}</span>
        ) : (
          <ul className="team-setup-toast-list">
            {messages.map((e, index) => (
              <li key={index}>{e.summary || e.message}</li>
            ))}
          </ul>
        )}
        {hasDetails && (
          <Button size="small" onClick={() => showTeamSetupDetails(messages)}>
            View details
          </Button>
        )}
      </div>
    ),
  });
};

// The endpoint answers `true` when the team has no message, so a non-array is an empty list.
export const displayValidationErrorMessages = (errors: unknown, canManageIntegrations: boolean) => {
  const messages: ValidationMessage[] = Array.isArray(errors) ? errors : [];
  const integrationKeyErrors = messages.filter((e) => e?.integration_key);
  if (integrationKeyErrors.length) {
    displayIntegrationKeyErrors(integrationKeyErrors, canManageIntegrations);
  } else {
    closeIntegrationToast();
  }
  displayTeamSetupMessages(messages.filter((e) => !e?.integration_key));
};
